import { h, Component, Fragment, ComponentType } from "preact";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Config from "../../config";
import { PaymentGateway, ProcessorComponent, ProcessorDesc, ProcessorRef, ProcessorsListResponse, ProcessorTypes } from "../../../../services/payment-gateway";
import RedeemVoucher, { VouchersPageEmbedded } from "../../mobile-components/vouchers/redeem-voucher";
import CreateVoucher from "../../mobile-components/vouchers/create-voucher";
import * as U from "../../desktop-templates/menu-user";
import User from "../../../../services/user";
import { WalletsPanel } from "../../mobile-components/site/menu-right";
import formatCurrency from "../../../../services/currency";

// --- Import Concrete Implementations ---
import { useDepositTransaction } from "./payment-gateway/deposit/logic";
import { DepositAmountPage, CheckPhonePage } from "./payment-gateway/deposit/components";
import { useWithdrawTransaction } from "./payment-gateway/withdraw/logic";
import { WithdrawAmountPage, ProcessingWithdrawPage } from "./payment-gateway/withdraw/components";
import { Spinner, ErrorMessage, GatewayContainer, SuccessPage, WaitSuccessPage, FullSizeIframe } from "./payment-gateway/shared/components";

// --- Import Dependency Contracts ---
import { DepositPageDependencies, WithdrawPageDependencies, GatewaySelectorDependencies } from "./payment-gateway/dependencies";

import AwrabetPaymentGatewayPage from "../../../awrabet/desktop-components/user/menu-payment-gateway";
import { P2PPage } from "../../../awrabet/mobile-components/p2p/p2p-pages";
import { TelegramDepositPage, TelegramWithdrawPage } from "../../../awrabet/mobile-components/p2p/telegram-pages";
import { TelegramIcon } from "../../../awrabet/utils/icons";

// --- TYPE DEFINITIONS ---
export type TabName = ProcessorTypes;

interface Tab {
	label: string,
	description: string,
}

type GatewaySelectorSteps = 'start' | 'provider';

interface GatewaySelectorState {
	page: GatewaySelectorSteps;
	services: ProcessorsListResponse;
	provider: ProcessorRef;
}

interface GatewayRowProps {
	row: ProcessorRef;
	choseProvider: (row: ProcessorRef) => void;
}

// --- FACTORIES ---

const createDepositPage = (dependencies: DepositPageDependencies): ComponentType<ProcessorRef> => (props: ProcessorRef) => {
	const { useDepositTransaction, DepositAmountPage, CheckPhonePage, Spinner, ErrorMessage, GatewayContainer, SuccessPage, WaitSuccessPage, FullSizeIframe } = dependencies;
	const { step, error, formUrl, statusUrl, initiateTransaction, handleTransactionStatus, reset } = useDepositTransaction({ provider: props });

	const renderStep = () => {
		switch (step) {
			case 'start':
				return <Fragment>
					<DepositAmountPage
						requestDeposit={initiateTransaction}
						showPhoneNumber={<div class="w-full text-sm text-slate-500 pt-1 text-right">
							<span><Trans>Phone Number</Trans>:</span> +251{User.username()}
						</div>}
					/>
					<GatewayContainer>
						<div class="text-left text-xs text-slate-800">
							<div class="text-base font-bold">ገቢ በተመለከተ</div>
							<div class="pt-1">በዋሌቶ አሁን ያለዎት ሂሳብ</div>
							<div class="pt-1">
								<WalletsPanel
									balance={formatCurrency(User.balance())}
									payable={formatCurrency(User.payable())}
									bonus={formatCurrency(User.bonus())}
								/>
							</div>
							{false && <p class="pt-1">ገቢ ማድረግ የሚፈልጉትን የብር መጠን ባዶው ቦታ ላይ በማስገባትና Deposit የሚለውን በመጫን ገቢ ማድረግ ይችላሉ።
								<ul class="pt-0.5 pl-4 list-disc">
									<li>ገቢ ለማድረግ ምንም አይነት ኮሚሽን አይከፍሉም</li>
									<li>ገቢ የማድረግ ጥያቄዎች ክፍያ ሲፈፅሙ ወዲያውኑ በፍጥነት የሚከናወኑ ሲሆን አንድ አንዴ እስከ 5 ደቂቃ ሊፈጅ ይችላል።</li>
									<li>በክፍያ አገልግሎቶች መቋረጥ ምክንያት የተያዙ ወጪዎች በ12 ሰዓት ውስጥ መፍትሄ እንዲያገኙ ይደረጋል</li>
									<li>ማንኛው ችግር ሲገጥምዎ በስልክ ወይም ቴሌግራም 9353 ማግኘት ይችላሉ።</li>
								</ul>
							</p>}
						</div>
					</GatewayContainer>
				</Fragment>;
			case 'wait':
				return <Spinner />;
			case 'error':
				return <ErrorMessage error={error!} />;
			case 'external-pay-form':
				return <FullSizeIframe formurl={formUrl!} />;
			case 'pending':
				return <CheckPhonePage statusUrl={statusUrl!} onEvent={handleTransactionStatus} />;
			case 'success-wait':
				return <WaitSuccessPage />;
			case 'success':
				return <SuccessPage onRepeat={reset} />;
			default:
				return null;
		}
	};

	return <div class="grow w-full flex flex-col items-center text-slate-800 bg-linear-to-br from-fuchsia-300 to-blue-300 overflow-auto">
		{renderStep()}
	</div>;
};

const createWithdrawPage = (dependencies: WithdrawPageDependencies): ComponentType<ProcessorRef> => (props: ProcessorRef) => {
	const { useWithdrawTransaction, WithdrawAmountPage, ProcessingWithdrawPage, Spinner, ErrorMessage, GatewayContainer, SuccessPage } = dependencies;
	const { step, error, statusUrl, initiateTransaction, handleTransactionStatus, reset } = useWithdrawTransaction({ provider: props });

	const renderStep = () => {
		switch (step) {
			case 'start':
				return <Fragment>
					<WithdrawAmountPage
						requestWithdraw={initiateTransaction}
						showMaxAmount={{
							altDesc: 'ወጪ ማድረግ የሚችሉት የብር መጠን',
							description: <Trans>Max Withdrawable</Trans>,
							value: User.payable()
						}}
					/>
					<GatewayContainer>
						<div class="text-left text-xs text-slate-800">
							<div class="text-base font-bold">ወጪ በተመለከተ</div>
							<div class="pt-1">በዋሌቶ አሁን ያለዎት ሂሳብ</div>
							<div class="pt-1">
								<WalletsPanel
									balance={formatCurrency(User.balance())}
									payable={formatCurrency(User.payable())}
									bonus={formatCurrency(User.bonus())}
								/>
							</div>
							{false && <p class="pt-1">በቶቶ ወጪ ለመጠየቅ ከላይ በሚገኘው ባዶ ቦታ ላይ ወጪ ማድረግ የሚፈልጉትን የብር መጠን በማስገባት የሚለውን ሲጫኑ በመረጡት የክፍያ አማራጭ በተመዘገቡት ስልክ ቁጥር አማካኝነት ገቢ ይሆናል።
								<ul class="pt-0.5 pl-4 list-disc">
									<li>ወጪ ጥያቄዎች ወዲያውኑ በፍጥነት የሚከናወኑ ሲሆን አንድ አንዴ እስከ 5 ደቂቃ ሊፈጅ ይችላል።</li>
									<li>በክፍያ አገልግሎቶች መቋረጥ ምክንያት የተያዙ ወጪዎች በ12 ሰዓት ውስጥ መፍትሄ እንዲያገኙ ይደረጋል</li>
									<li>ማንኛው ችግር ሲገጥምዎ በስልክ ወይም ቴሌግራም 9353 ማግኘት ይችላሉ።</li>
								</ul>
							</p>}
						</div>
					</GatewayContainer>
				</Fragment>;
			case 'wait':
				return <Spinner />;
			case 'error':
				return <ErrorMessage error={error!} />;
			case 'pending':
				return <ProcessingWithdrawPage statusUrl={statusUrl!} onEvent={handleTransactionStatus} />;
			case 'success':
				return <SuccessPage onRepeat={reset} />;
			default:
				return null;
		}
	};

	return <div class="grow w-full flex flex-col items-center text-slate-800 bg-linear-to-br from-fuchsia-300 to-blue-300 overflow-auto">
		{renderStep()}
	</div>;
};

const createGatewaySelector = (dependencies: GatewaySelectorDependencies): ComponentType<{ tab: TabName }> => {
    const { DepositPage, WithdrawPage } = dependencies;

    return class GatewaySelector extends Component<{ tab: TabName }, GatewaySelectorState> {
        service = new PaymentGateway(Config.softionAPI);
        state: GatewaySelectorState = {
            page: 'start',
            services: null as ProcessorsListResponse,
            provider: null as ProcessorRef
        };

        customPages: { [key: string]: ProcessorDesc } = {
			p2p: {  processor: 'p2p', provider: 'exchange', name: 'Exchange', deposit: () => <P2PPage defaultPage="offers" />, withdraw: () => <P2PPage defaultPage="withdraw" />, bonusPerc: 100 },
            voucher: { processor: 'voucher', provider: 'voucher', name: 'Voucher', deposit: RedeemVoucher, withdraw: CreateVoucher },
            ...(Config.allowP2P ? { telegram: { processor: 'telegram', provider: 'telegram', name: 'Telegram', deposit: TelegramDepositPage, withdraw: TelegramWithdrawPage } } : {}),
        };

        componentWillMount(): void {
            this.service.requestServicesList().then(response => {
                this.setState({ services: response });
            });
        }

        asProcessorDesc = (proc: ProcessorRef): ProcessorDesc => {
            return { ...proc, withdraw: WithdrawPage, deposit: DepositPage };
        }

        getProviders(): ProcessorsListResponse {
            const services: ProcessorsListResponse = this.state.services || { withdraw: [], deposit: [] };
            return {
                withdraw: [...services.withdraw.map(this.asProcessorDesc), ...Object.values(this.customPages)],
                deposit: [...services.deposit.map(this.asProcessorDesc), ...Object.values(this.customPages)],
            }
        }

        setProvider = (provider: ProcessorRef) => {
            this.setState({ page: 'provider', provider: provider })
        }

        render() {
            if (this.state.page == 'start') {
                return <div class="grow w-full flex flex-col items-center pt-3 text-slate-800">
                    <div class="text-xl">{this.props.tab == 'deposit' ? <Trans>Deposit</Trans> : <Trans>Withdraw</Trans>}</div>
					{false && <div class="w-full max-w-sm p-4 my-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded-lg shadow-md" role="alert">
						<p class="font-bold">Dear Harifsport Family,</p>
						<p class="text-sm mb-2">
							Deposits and withdrawals are temporarily unavailable. Our team is actively working to restore full functionality as quickly as possible.
							We appreciate your patience.
						</p>
						<hr class="my-2 border-yellow-300" />
						<p class="font-bold">ዉድ የ ሃሪፍ ስፖርት ቤተሰቦች፣</p>
						<p class="text-sm">
							የተቀማጭ እና የወጪ ክፍያዎች አገልግሎት ለጊዜዉ የተቋረጡ ሲሆን አገልግሎቱን መስጠት ስንጀምር የምናሳውቅዎት መሆኑን ከይቅርታ ጋር ለመግለጽ እንወዳለን።
							ስለትዕግስትዎ እናመሰግናለን።
						</p>
					</div>}
                    <div class="flex flex-col items-center w-full max-w-sm backdrop-blur-xl bg-white rounded p-4 gap-2">
                        {this.getProviders()[this.props.tab].map(
                            row => <GatewayRow row={row} choseProvider={this.setProvider} />
                        )}
                    </div>
                </div>
            }
            const GatewayInnerPage: ProcessorComponent = this.state.provider[this.props.tab];
            return <GatewayInnerPage key={this.state.provider.provider} provider={this.state.provider.provider} processor={this.state.provider.processor} name={this.state.provider.name} />;
        }
    }
};

// --- COMPOSITION ROOT ---

const depositPageDependencies: DepositPageDependencies = {
    useDepositTransaction,
    DepositAmountPage,
    CheckPhonePage,
    Spinner,
    ErrorMessage,
    GatewayContainer,
    SuccessPage,
    WaitSuccessPage,
    FullSizeIframe,
};

const withdrawPageDependencies: WithdrawPageDependencies = {
    useWithdrawTransaction,
    WithdrawAmountPage,
    ProcessingWithdrawPage,
    Spinner,
    ErrorMessage,
    GatewayContainer,
    SuccessPage,
};

const DepositPage = createDepositPage(depositPageDependencies);
const WithdrawPage = createWithdrawPage(withdrawPageDependencies);

const gatewaySelectorDependencies: GatewaySelectorDependencies = {
    DepositPage,
    WithdrawPage,
};

const GatewaySelector = createGatewaySelector(gatewaySelectorDependencies);

// --- MAIN EXPORTED COMPONENT ---

export default class PaymentGatewayPage extends AwrabetPaymentGatewayPage {
	render() {
		return <div class="grow h-full min-h-[inherit] flex flex-col">
			<U.SubTabsContainer>
				{Object.entries(this.tabs).filter(([k, tab]) => tab.visible).map(([key, item]: [TabName, Tab]) => {
					return <U.Tab selected={key === this.state.current} onClick={() => this.switchTabs(key)} >{item.label}</U.Tab>;
				})}
			</U.SubTabsContainer>
			<div class="user-menu-header main-text-color p-2 text-base font-bold m-3 capitalize rounded hidden">{this.tabs[this.state.current].description}</div>
			{this.state.current == 'deposit' || this.state.current == 'withdraw' ?
				<GatewaySelector key={this.state.current} tab={this.state.current} /> : null}
			{this.state.current == 'vouchers' ? 
				<VouchersPageEmbedded />
			: null}
			{this.state.current == 'p2p-deposits' ? 
				<P2PPage defaultPage="my-purchases" />
			: null}
			{this.state.current == 'p2p-withdraws' ? 
				<P2PPage defaultPage="my-offers" />
			: null}
		</div>;
	}
}


// --- STATIC ASSETS & HELPERS ---

const processorIcons = {
	telebirr: require('url:../../assets/img/logo_provider/telebirr.png'),
	telebirrussd: require('url:../../assets/img/logo_provider/telebirr.png'),
	cbebirr: require('url:../../assets/img/logo_provider/cbebirr.png'),
	cbe: require('url:../../assets/img/logo_provider/cbebirr.png'),
	mpesa: require('url:../../assets/img/logo_provider/mpesa-logo.png'),
	exchange: require('url:../../../awrabet/assets/img/logo_provider/p2p.svg'),
	voucher: require('url:../../assets/img/logo_provider/voucher.svg'),
};

function GatewayRow({ choseProvider, row }: GatewayRowProps) {
    const providerTag = row.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const isExchange = row.provider === 'exchange' && row.bonusPerc > 0;
// il css misto così è orripilante.
    return <a
        class={`w-full rounded shadow-xl flex gap-1 px-3 py-2 h-24 items-start justify-between hover:scale-110 transition-transform relative ${isExchange ? 'gold-border' : 'border'}`}
        href="javascript:void(0);"
        onClick={() => choseProvider(row)}
    >
		<style>{`
@keyframes shimmer-border {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.gold-border {
    background:
        linear-gradient(white, white) padding-box,
        linear-gradient(90deg, #b8860b, #ffd700, #fffacd, #ffd700, #b8860b) border-box;
    background-size: 200% 200%;
    border: 2px solid transparent;
    animation: shimmer-border 3s ease infinite;
}
`}</style>
        {isExchange && (
            <span class="absolute top-1.5 left-1/2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap tracking-wide"
                style={{ color: '#b8860b' }}>
                {row.bonusPerc}% deposit bonus
            </span>
        )}
        <div class="h-full flex gap-1 items-center justify-start">
            {providerTag === 'telegram'
                ? <TelegramIcon class="h-full" />
                : <img src={processorIcons[providerTag]} alt={row.processor} class="h-full" />
            }
        </div>
        <div class="grow font-bold text-xxs capitalize hidden">{row.name}</div>
        <div class="text-xs text-slate-600">
            <div>via {row.provider}</div>
        </div>
    </a>
}
